import React, { useEffect, useRef } from 'react';

import Accordion from 'components/Accordion';
import Checkbox from 'components/DesignSystem/Checkbox';
import RadioButton from 'components/DesignSystem/RadioButton';
import TitleSemiBold from 'components/Typography/TitleSemiBold';
import { OpcaoRmv } from 'helper/windx';

interface RmvProps {
  opcoesDisponiveis: OpcaoRmv[];
  // WINDX-030 (contrato v7.0): substitui `valor: OpcaoRmv | undefined`.
  // Em WEOL-SH eh checkbox multi (1 ou 2 itens). Em WEOL-SM/Comparativo,
  // sempre length=1 com apenasComRmv=true.
  valor: OpcaoRmv[];
  onChange: (valor: OpcaoRmv[]) => void;
  // FE-LAYOUT-009 (WEOL-SM): quando true, renderiza so o radio `com_rmv`.
  // Quando false (default — WEOL-SH), renderiza ambos como CHECKBOX (multi).
  apenasComRmv?: boolean;
}

const LABELS: Record<OpcaoRmv, string> = {
  com_rmv: 'Com RMV',
  sem_rmv: 'Sem RMV',
};

const TODAS_OPCOES: OpcaoRmv[] = ['com_rmv', 'sem_rmv'];

export default function Rmv({
  opcoesDisponiveis,
  valor,
  onChange,
  apenasComRmv = false,
}: RmvProps) {
  // WINDX-040: auto-select APENAS quando opcoesDisponiveis muda E valor
  // ficou vazio. Antes (FE-LAYOUT-009): useEffect rodava sempre que valor
  // mudava — revertia clique explicito do operador em "Sem RMV" quando o
  // modelo so tinha com_rmv disponivel.
  // Agora: prevOpcoesRef inicia como `null` (sentinel: primeiro render);
  // dispara onChange so quando (a) primeiro render OU (b) opcoesDisponiveis
  // efetivamente mudou, E valor esta vazio. Clique explicito do operador
  // em "Sem RMV" indisponivel nao eh revertido — BE retorna 0 series e FE
  // mostra empty state.
  const prevOpcoesRef = useRef<OpcaoRmv[] | null>(null);
  useEffect(() => {
    const anterior = prevOpcoesRef.current;
    const opcoesMudaram = anterior === null || (
      anterior.length !== opcoesDisponiveis.length
      || anterior.some((o, i) => opcoesDisponiveis[i] !== o)
    );
    prevOpcoesRef.current = opcoesDisponiveis;

    if (!opcoesMudaram) return;
    if (opcoesDisponiveis.length !== 1) return;
    if (valor.length !== 0) return; // Operador ja escolheu — nao tocar.

    onChange([opcoesDisponiveis[0]]);
  }, [opcoesDisponiveis, valor, onChange]);

  // FE-LAYOUT-009 (2026-05-17): componente sempre visivel. Antes:
  // `if (opcoesDisponiveis.length === 0) return null;`
  const opcoesParaRenderizar: OpcaoRmv[] = apenasComRmv ? ['com_rmv'] : TODAS_OPCOES;

  // WINDX-030 (WEOL-SH): toggle de checkbox. Garante min 1 selecionado
  // (tentativa de desmarcar a ultima eh ignorada — feedback silencioso).
  const toggleCheckbox = (opt: OpcaoRmv) => {
    if (valor.includes(opt)) {
      if (valor.length <= 1) return; // protege contra estado vazio
      onChange(valor.filter((v) => v !== opt));
    } else {
      onChange([...valor, opt]);
    }
  };

  return (
    <Accordion.Item eventKey="rmv">
      <Accordion.Header>
        <TitleSemiBold>RMV</TitleSemiBold>
      </Accordion.Header>
      <Accordion.Body>
        {opcoesParaRenderizar.map((opt) => {
          // WINDX-038 (2026-05-25): checkboxes RMV em WEOL-SH agora sempre
          // habilitados — operador pode marcar 'sem_rmv' mesmo se nenhum modelo
          // selecionado tiver essa variante. BE retorna 0 series naquele caso,
          // FE renderiza empty state existente (GraficoSH.tsx:171).
          // Antes (FE-LAYOUT-009): aplicava disabled={indisponivel} para
          // bloquear UX, mas isso impedia operador de explorar combinacao.
          const marcado = valor.includes(opt);

          // WINDX-030: WEOL-SM mantem RadioButton (1 unica opcao sempre).
          // WEOL-SH passa a usar Checkbox (multi-select com toggle).
          if (apenasComRmv) {
            return (
              <RadioButton
                key={opt}
                id={`windx-rmv-${opt}`}
                name="windx-rmv"
                label={LABELS[opt]}
                checked={marcado}
                onChange={() => onChange([opt])}
              />
            );
          }
          return (
            <Checkbox
              key={opt}
              id={`windx-rmv-${opt}`}
              label={LABELS[opt]}
              checked={marcado}
              onChange={() => toggleCheckbox(opt)}
            />
          );
        })}
      </Accordion.Body>
    </Accordion.Item>
  );
}
